import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, Search, Menu, X, User, Trash2, Plus, Minus } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useUser } from '../context/UserContext';
import { categories } from '../data/products';
import LoginModal from './LoginModal';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [logoError, setLogoError] = useState(false);
  const [brandLogoError, setBrandLogoError] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const { state, removeFromCart, updateQuantity, clearCart } = useCart();
  const { user, logout } = useUser();
  const navigate = useNavigate();

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery)}`);
      setSearchQuery('');
    }
  };

  return (
    <>
      <header className="bg-[#F6F4EF] shadow-sm border-b border-[#DEDACF] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-0.5">
              
              {brandLogoError ? (
                <span className="text-xl font-bold text-gray-900">c nine</span>
              ) : (
                <img 
                  src={process.env.PUBLIC_URL + '/brand-logo.png'}
                  alt="c nine brand" 
                  className="h-10 md:h-14 w-auto object-contain max-w-[200px] md:max-w-[250px]"
                  style={{ imageRendering: 'crisp-edges' }}
                  onError={() => setBrandLogoError(true)}
                />
              )}
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex space-x-8">
              <Link 
                to="/" 
                className="text-gray-700 font-medium transition-colors"
                style={{ '--hover-color': '#4554a4' } as React.CSSProperties}
                onMouseEnter={(e) => e.currentTarget.style.color = '#4554a4'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#374151'}
              >
                Home
              </Link>
              <Link 
                to="/products" 
                className="text-gray-700 font-medium transition-colors"
                onMouseEnter={(e) => e.currentTarget.style.color = '#4554a4'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#374151'}
              >
                All Products
              </Link>
              <div className="relative group">
                <button 
                  className="text-gray-700 font-medium transition-colors flex items-center"
                  onMouseEnter={(e) => e.currentTarget.style.color = '#4554a4'}
                  onMouseLeave={(e) => e.currentTarget.style.color = '#374151'}
                >
                  Categories
                </button>
                <div className="absolute top-full left-0 mt-2 w-[320px] bg-white rounded-lg shadow-lg border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                  <div className="p-4">
                    <div className="grid grid-cols-2 gap-3">
                      {categories.map((category) => (
                        <Link
                          key={category.id}
                          to={`/products?category=${category.id}`}
                          className="flex items-center space-x-2 p-2 rounded transition-colors"
                          style={{ '--hover-bg': '#f3f4f6' } as React.CSSProperties}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#4554a4';
                            e.currentTarget.style.color = '#fff';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = '#374151';
                          }}
                        >
                          <span className="text-lg flex-shrink-0">{category.icon}</span>
                          <span className="text-sm font-medium whitespace-nowrap">{category.name}</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </nav>

            {/* Search Bar */}
            <div className="hidden md:flex flex-1 max-w-md mx-8">
              <form onSubmit={handleSearch} className="w-full">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search products..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:border-transparent transition-colors"
                    style={{ 
                      '--focus-ring': '#4554a4'
                    } as React.CSSProperties}
                    onFocus={(e) => {
                      e.currentTarget.style.boxShadow = '0 0 0 2px rgba(69, 84, 164, 0.2)';
                      e.currentTarget.style.borderColor = '#4554a4';
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.boxShadow = '';
                      e.currentTarget.style.borderColor = '#d1d5db';
                    }}
                  />
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                </div>
              </form>
            </div>

            {/* Right side icons */}
            <div className="flex items-center space-x-4">
              {/* User Icon or Initials */}
              {user ? (
                <div className="relative">
                  <button
                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                    className="w-8 h-8 rounded-full bg-primary-600 text-white flex items-center justify-center font-semibold text-sm transition-transform hover:scale-105"
                    title="My Account"
                  >
                    {user.firstName[0].toUpperCase()}{user.lastName[0].toUpperCase()}
                  </button>
                  
                  {isUserMenuOpen && (
                    <>
                      <div 
                        className="fixed inset-0 z-40" 
                        onClick={() => setIsUserMenuOpen(false)}
                      ></div>
                      <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
                        <button
                          onClick={() => {
                            setIsUserMenuOpen(false);
                            navigate('/profile');
                          }}
                          className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                        >
                          Profile
                        </button>
                        <button
                          onClick={() => {
                            setIsUserMenuOpen(false);
                            if (window.confirm('Do you want to log out?')) {
                              clearCart();
                              logout();
                              navigate('/');
                            }
                          }}
                          className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                        >
                          Log Out
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <button 
                  onClick={() => navigate('/login')}
                  className="p-2 text-gray-600 transition-colors"
                  title="Login / Sign Up"
                  onMouseEnter={(e) => e.currentTarget.style.color = '#4554a4'}
                  onMouseLeave={(e) => e.currentTarget.style.color = '#4b5563'}
                >
                  <User className="w-5 h-5" />
                </button>
              )}

              {/* Cart */}
              <button
                onClick={() => setIsCartOpen(true)}
                className="relative p-2 text-gray-600 transition-colors"
                onMouseEnter={(e) => e.currentTarget.style.color = '#4554a4'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#4b5563'}
              >
                <ShoppingCart className="w-5 h-5" />
                {state.totalItems > 0 && (
                  <span 
                    className="absolute -top-1 -right-1 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center"
                    style={{ backgroundColor: '#4554a4' }}
                  >
                    {state.totalItems}
                  </span>
                )}
              </button>

              {/* Mobile menu button */}
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="md:hidden p-2 text-gray-600 transition-colors"
                onMouseEnter={(e) => e.currentTarget.style.color = '#4554a4'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#4b5563'}
              >
                {isMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Mobile Search */}
          <div className="md:hidden pb-4">
            <form onSubmit={handleSearch}>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:border-transparent transition-colors"
                    onFocus={(e) => {
                      e.currentTarget.style.boxShadow = '0 0 0 2px rgba(69, 84, 164, 0.2)';
                      e.currentTarget.style.borderColor = '#4554a4';
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.boxShadow = '';
                      e.currentTarget.style.borderColor = '#d1d5db';
                    }}
                />
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              </div>
            </form>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-200">
            <div className="px-4 py-4 space-y-4">
              <Link
                to="/"
                className="block text-gray-700 font-medium transition-colors"
                onClick={() => setIsMenuOpen(false)}
                onMouseEnter={(e) => e.currentTarget.style.color = '#4554a4'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#374151'}
              >
                Home
              </Link>
              <Link
                to="/products"
                className="block text-gray-700 font-medium transition-colors"
                onClick={() => setIsMenuOpen(false)}
                onMouseEnter={(e) => e.currentTarget.style.color = '#4554a4'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#374151'}
              >
                All Products
              </Link>
              <div className="space-y-2">
                <div className="text-gray-700 font-medium">Categories</div>
                <div className="grid grid-cols-2 gap-2 ml-4">
                  {categories.map((category) => (
                    <Link
                      key={category.id}
                      to={`/products?category=${category.id}`}
                      className="flex items-center space-x-2 p-2 rounded transition-colors"
                      onClick={() => setIsMenuOpen(false)}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#4554a4';
                        e.currentTarget.style.color = '#fff';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = '#374151';
                      }}
                    >
                      <span className="text-lg">{category.icon}</span>
                      <span className="text-sm">{category.name}</span>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Cart Sidebar */}
      {isCartOpen && (
        <div className="fixed inset-0 z-50 overflow-hidden">
          <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setIsCartOpen(false)} />
          <div className="absolute right-0 top-0 h-full w-full sm:w-96 bg-white shadow-xl">
            <div className="flex flex-col h-full">
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <div className="flex items-center space-x-2">
                  <h2 className="text-lg font-semibold text-gray-900">Shopping Cart</h2>
                  {state.items.length > 0 && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 font-medium">
                      {state.totalItems} {state.totalItems === 1 ? 'item' : 'items'}
                    </span>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  {state.items.length > 0 && (
                    <button
                      onClick={() => {
                        if (window.confirm('Are you sure you want to clear your cart?')) {
                          clearCart();
                        }
                      }}
                      className="text-xs text-red-500 hover:text-red-700 hover:underline px-1 py-1 transition-colors font-medium"
                      title="Clear all items"
                    >
                      Clear All
                    </button>
                  )}
                  <button
                    onClick={() => setIsCartOpen(false)}
                    className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                    aria-label="Close cart"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
              
              <div className="flex-1 overflow-y-auto p-4">
                {state.items.length === 0 ? (
                  <div className="text-center text-gray-500 py-12">
                    <ShoppingCart className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p className="text-base font-medium text-gray-700">Your cart is empty</p>
                    <p className="text-xs text-gray-400 mt-1">Add items to get started!</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {state.items.map((item) => {
                      const fallbackImage = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjQwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNDAwIiBoZWlnaHQ9IjQwMCIgZmlsbD0iI2YzZjRmNiIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTgiIGZpbGw9IiM5Y2EzYWYiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5ObyBJbWFnZTwvdGV4dD48L3N2Zz4=';
                      const itemImage = item.selectedColorVariant?.image || (item.selectedColor && item.product.colorVariants?.find(v => v.name === item.selectedColor)?.image) || item.product.image || fallbackImage;
                      const itemPrice = (item.selectedColorVariant?.price !== undefined && item.selectedColorVariant.price > 0) ? item.selectedColorVariant.price : item.product.price;
                      const itemKey = `${item.product.id}-${item.selectedColor || 'default'}`;

                      return (
                        <div key={itemKey} className="flex items-start space-x-3 p-3 border border-gray-200 rounded-lg hover:border-gray-300 transition-colors bg-white">
                          <img
                            src={itemImage}
                            alt={item.product.title}
                            className="w-14 h-14 sm:w-16 sm:h-16 object-cover rounded flex-shrink-0 border border-gray-100"
                            onError={(e) => {
                              if (e.currentTarget.src !== fallbackImage) {
                                e.currentTarget.src = fallbackImage;
                              }
                            }}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex justify-between items-start">
                              <div>
                                <h3 className="text-sm font-medium text-gray-900 line-clamp-2 leading-tight">
                                  {item.product.title}
                                </h3>
                                {item.selectedColor && (
                                  <span className="inline-block text-[11px] font-semibold text-indigo-700 bg-indigo-50 border border-indigo-100 px-2 py-0.5 rounded mt-1">
                                    Color: {item.selectedColor}
                                  </span>
                                )}
                              </div>
                              <button
                                onClick={() => removeFromCart(item.product.id, item.selectedColor)}
                                className="text-gray-400 hover:text-red-600 transition-colors ml-2 p-1 rounded-full hover:bg-red-50 flex-shrink-0"
                                title="Remove item"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                            <p className="text-sm font-semibold text-gray-900 mt-1">₹{itemPrice}</p>
                            
                            <div className="flex items-center justify-between mt-2">
                              <div className="flex items-center space-x-2 border border-gray-200 rounded px-2 py-0.5 bg-gray-50">
                                <button
                                  onClick={() => updateQuantity(item.product.id, item.quantity - 1, item.selectedColor)}
                                  className="text-gray-500 hover:text-gray-700 transition-colors p-0.5"
                                  title="Decrease quantity"
                                >
                                  <Minus className="w-3.5 h-3.5" />
                                </button>
                                <span className="text-xs font-semibold w-5 text-center text-gray-900">{item.quantity}</span>
                                <button
                                  onClick={() => updateQuantity(item.product.id, item.quantity + 1, item.selectedColor)}
                                  className="text-gray-500 hover:text-gray-700 transition-colors p-0.5"
                                  title="Increase quantity"
                                >
                                  <Plus className="w-3.5 h-3.5" />
                                </button>
                              </div>
                              <span className="text-xs font-medium text-gray-500">
                                Total: ₹{itemPrice * item.quantity}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              
              {state.items.length > 0 && (
                <div className="border-t border-gray-200 p-4 bg-white">
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-base sm:text-lg font-semibold text-gray-900">Total:</span>
                    <span className="text-base sm:text-lg font-semibold" style={{ color: '#4554a4' }}>₹{state.totalPrice}</span>
                  </div>
                  <button 
                    onClick={() => {
                      if (!user) {
                        setShowLoginModal(true);
                      } else {
                        setIsCartOpen(false);
                        navigate('/checkout');
                      }
                    }}
                    className="w-full text-white py-3 px-4 rounded-lg transition-colors font-medium text-sm sm:text-base"
                    style={{ backgroundColor: '#4554a4' }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#374a8a'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#4554a4'}
                  >
                    Proceed to Checkout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showLoginModal && (
        <LoginModal 
          onClose={() => setShowLoginModal(false)}
          onSuccess={() => {
            setShowLoginModal(false);
            setIsCartOpen(false);
            navigate('/checkout');
          }}
        />
      )}
    </>
  );
};

export default Header;
